import React, { useState } from 'react';
import styled from 'styled-components';
import { Icon } from 'components/common';
import { colours } from 'utils/theme';
import CollapsiblePanel from '../CollapsiblePanel';

const Container = styled.div`
  font-size: 14px;
  border: 1px solid ${p => p.theme.lightGrey};
  border-radius: 4px;
  padding: 10px;
`;

const Title = styled.div`
  user-select: none;
  font-weight: 500;
  cursor: pointer;
  padding: 10px 0;
  display: flex;
  margin-left: 10px;
  > div {
    width: 30px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

const Content = styled.div`
  padding: 0px 10px 10px;
  > * {
    margin-bottom: 10px;
    :last-child {
      margin-bottom: 5px;
    }
  }
`;

const RotatingIconWrapper = styled.div`
  margin-left: auto;
  transform: ${p => (p.up ? 'rotate(180deg)' : 'rotate(0)')};
  transition: transform 0.2s linear;
`;

/**
 * A reusable accordion component that can be collapsed/expanded
 *
 * @param {Object} props
 * @param {string} props.title - The title shown in the accordion header
 * @param {React.ReactNode} props.children - Content to show when expanded
 * @param {string} [props.icon] - Optional icon to show next to title
 * @param {boolean} props.isCollapsed - Whether the accordion is collapsed
 * @param {(isCollapsed: boolean) => void} props.onToggle - Callback when collapsed state changes
 * @param {string} [props.testId] - Test ID for the component
 * @param {string} [props.iconColor] - Color for the icon if provided
 */
const Accordion = ({
  title,
  children,
  icon,
  isCollapsedByDefault = false,
  testId = 'accordion',
  iconColor = colours.darkGrey,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(isCollapsedByDefault);

  return (
    <Container data-testid={testId}>
      <Title
        data-testid={`${testId}-title`}
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        {icon && (
          <div>
            <Icon icon={icon} color={iconColor} size='sm' />
          </div>
        )}
        {title}
        <RotatingIconWrapper up={!isCollapsed}>
          <Icon icon='chevronDown' size='sm' color={colours.darkGrey} />
        </RotatingIconWrapper>
      </Title>
      <CollapsiblePanel isCollapsed={isCollapsed}>
        <Content>{children}</Content>
      </CollapsiblePanel>
    </Container>
  );
};

export default Accordion;
